import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {Stack} from '@mantine/core';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {FormPageLayout, PlatformLoader} from '@flexinet/ui-components';

import {Profile} from '../profile/Profile.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {EditPageNavigation} from '../navigation/EditPageNavigation.tsx';
import {usePricesSelection} from '../hooks/usePricesSelection.ts';
import {useEditPageTools} from '../hooks/useEditPageTools.tsx';
import {fetch as fetchPrice, update as updatePrice} from '../services/PricesService.ts';
import {Price} from '../types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const EditPage = () => {
    const {id: priceId} = useParams();

    if (typeof priceId === 'undefined') {
        throw new Error('priceId is required parameter');
    }

    const queryClient = useQueryClient();

    const priceQuery = useQuery({
        queryKey: ['price', priceId],
        queryFn: () => fetchPrice(priceId),
    });

    const updateMutation = useMutation({
        mutationFn: (values: Price) => updatePrice(priceId, values),
        onSuccess: () => {
            notifySuccess('Price updated');
            queryClient.invalidateQueries({queryKey: ['price', priceId]});
            queryClient.invalidateQueries({queryKey: ['prices']});
        },
        onError: notifyError,
    });

    const {handleClearSelection} = usePricesSelection();

    const [drawerOpened, setDrawerOpened] = useState(false);
    const {tools} = useEditPageTools({onEdit: () => setDrawerOpened(true)});

    useEffect(() => {
        handleClearSelection();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (priceQuery.isLoading || !priceQuery.data) {
        return <PlatformLoader message='Loading price...Please wait'/>;
    }

    const price = priceQuery.data;

    const handleDrawerSubmit = (values: Price) => {
        updateMutation.mutate(values);
    };

    return (
        <FormPageLayout
            title={price.id}
            pageNavigation={<EditPageNavigation component={price}/>}
            tools={tools}
        >
            <Stack gap='lg'>
                <Profile record={price}/>
            </Stack>

            <FormDrawer
                record={price}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </FormPageLayout>
    );
};
